/** @jsxImportSource @emotion/react */

import { css } from '@emotion/react';

import { Box, Button, CircularProgress, Dialog, IconButton, List, ListItemButton, Theme as MaterialTheme, Paper, Popover, PopoverOrigin, Popper, Stack, Tab, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tabs, TextField, Typography, useTheme, } from "@mui/material";
import Grid from '@mui/material/Unstable_Grid2';

import { useTranslation } from 'react-i18next';
import { Dispatch, MutableRefObject, SetStateAction, useEffect, useState } from 'react';
import AccountBusinessLogic from '../../../businessLogic/AccountBusinessLogic';
import { IAccountGuiModel } from '../../../model/IAccountGuiModel';
import { Close } from '@mui/icons-material';
import { digiAccActorType } from '../../../common/TextTypes';

interface IDialogProps{
    organizationId: string | undefined,
    dialogOpen: boolean,
    handleDialogClose: () => void,
    setAccount: Dispatch<SetStateAction<IAccountGuiModel | undefined>>,
    handleNewAccount: (newAccount: IAccountGuiModel) => void,
}
interface IPanelProps {
    index: number,
    value: number,
    id: string,
    list: IAccountGuiModel[],
    type: digiAccActorType | "generalLedger" | "other",
    handleClick: (listIndex: number, type: digiAccActorType | "generalLedger" | "other") => void,
}

const AccountPanel = (props: IPanelProps): JSX.Element => {

    const { t, i18n } = useTranslation()



    const {value, index, id, list, type, handleClick, ...other } = props;

    return <div role="tabpanel" hidden={value !== index} id={id} {...other}>
        {value === index && (
            <TableContainer sx={{maxHeight: "15rem"} }>
                <Table stickyHeader size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>{t("basic.accountNumber")}</TableCell>
                            <TableCell>{t("basic.name")}</TableCell>
                            <TableCell>{t("basic.bank")}</TableCell>
                            <TableCell>{t("basic.businessNumber")}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody component={List}>
                        {list.map((e, index) =>
                            <TableRow component={ListItemButton} onClick={() => handleClick(index, type) }>
                                <TableCell>
                                    {e.accountId}
                                </TableCell>
                                <TableCell>
                                    {e.name}
                                </TableCell>
                                <TableCell align="right">
                                    {e.bankAccount}
                                </TableCell>
                                <TableCell>
                                    {e.businessNumber}
                                </TableCell>
                            </TableRow>)}
                    </TableBody>
                </Table>
            </TableContainer>
        )}
    </div>
}

const DialogAccountSearch = (props: IDialogProps): JSX.Element => {

    const { organizationId, dialogOpen, handleDialogClose, setAccount, handleNewAccount } = props;

    const theme = useTheme();
    const { t, i18n } = useTranslation();

    const [value, setValue] = useState(0);

    const [loader, setLoader] = useState(false);

    const [freeText, setFreeText] = useState("");

    const [allAccountsList, setAllAccountsList] = useState<IAccountGuiModel[]>([]);
    const [accountsListError, setAccountsListError] = useState<string[]>([]);

    const [customerList, setCustomerList] = useState<IAccountGuiModel[]>([]);
    const [supplierList, setSupplierList] = useState<IAccountGuiModel[]>([]);
    const [generalLedgerList, setGeneralLedgerList] = useState<IAccountGuiModel[]>([]);

    const handleRowClick = (listIndex: number, type: digiAccActorType | "generalLedger" | "other") => {
        if (type === "Customer") {
            setAccount(customerList[listIndex]);
            handleNewAccount(customerList[listIndex])
            handleInnerDialogClose();
        } else if (type === "Supplier") {
            setAccount(supplierList[listIndex]);
            handleNewAccount(supplierList[listIndex])
            handleInnerDialogClose();
        } else if (type === "generalLedger") {
            setAccount(generalLedgerList[listIndex]);
            handleNewAccount(generalLedgerList[listIndex])
            handleInnerDialogClose();
        } else {
            setAccount(allAccountsList[listIndex]);
            handleNewAccount(allAccountsList[listIndex])
            handleInnerDialogClose();
        }
    }

    const handleInnerDialogClose = () => {
        setFreeText("");
        handleDialogClose();
    }

    useEffect(() => {

        let isMounted = true; //Avoid memory leaks. Async function running after dismount.

        const getAccounts = async () => {

            let logic = new AccountBusinessLogic();
            setLoader(true);
            const result = await logic.getAccounts(organizationId ? organizationId : "", "", freeText);
            setLoader(false);
            if (result.hasErrors) {
                if (isMounted) setAccountsListError(result.errors);
            } else {
                if (isMounted) setAccountsListError([]);
                if (isMounted) {
                    setAllAccountsList(result.value);
                    setCustomerList(result.value.filter(e => e.accountType === "Customer"));
                    setSupplierList(result.value.filter(e => e.accountType === "Supplier"));
                    setGeneralLedgerList(result.value.filter(e => e.accountType === "GeneralLedger"));
                }
            }
        }

        getAccounts();

        return () => { isMounted = false };

    }, [freeText, organizationId])

    const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
        setValue(newValue);
    }; 

    return <Dialog
        open={dialogOpen}
        onClose={handleInnerDialogClose}>
        {accountsListError.length > 0 ? <Typography>{t("basic.errorWithAccounts") + accountsListError.join(", ")}</Typography> :
            <Box component={Paper} sx={{ border: 1, borderColor: 'divider' }}>
                <Stack direction="column">
                    <Box padding={theme.spacing(3) }>
                        <TextField
                            size="small"
                            fullWidth
                            label={t("search.search")}
                            variant="outlined"
                            color="warning"
                            value={freeText}
                            onChange={(e) => setFreeText(e.target.value)}

                            id="freeTextId" />
                    </Box>
                    <Stack direction="row" justifyContent="space-between" sx={{ borderBottom: 1, borderColor: 'divider' }}>
                        <Tabs value={value} onChange={handleTabChange} aria-label="Account tab">
                            <Tab label={t("basic.customer")} id="customerTab" />
                            <Tab label={t("basic.supplier")} id="supplierTab" />
                            <Tab label={t("basic.generalLedger")} id="generalLedgerTab" />
                            <Tab label={t("basic.any")} id="anyTab" />
                        </Tabs>
                        <IconButton onClick={handleDialogClose}><Close /></IconButton>
                    </Stack>
                </Stack>
                {loader ? <CircularProgress color="inherit" /> : <>
                    <AccountPanel value={value} index={0} id="customerTab" list={customerList} type="Customer" handleClick={handleRowClick} />
                    <AccountPanel value={value} index={1} id="supplierTab" list={supplierList} type="Supplier" handleClick={handleRowClick} />
                    <AccountPanel value={value} index={2} id="generalLedgerTab" list={generalLedgerList} type="generalLedger" handleClick={handleRowClick} />
                    <AccountPanel value={value} index={3} id="anyTab" list={allAccountsList} type="other" handleClick={handleRowClick} />
                </>}
            </Box>}
    </Dialog>
}

export default DialogAccountSearch;